import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";

interface SiteCase {
  id: string;
  title: string;
  subtitle: string | null;
  slug: string;
  content: string;
  cover_image_url: string | null;
  tags: string[] | null;
  is_published: boolean;
  author_id?: string;
  featured_on_landing?: boolean;
  updated_at?: string;
}

interface CaseFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCase: SiteCase | null;
  onSuccess: () => void;
}

export function CaseFormModal({ open, onOpenChange, editingCase, onSuccess }: CaseFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    slug: "",
    content: "",
    cover_image_url: "",
    tags: [] as string[],
    is_published: false,
    featured_on_landing: false,
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    if (editingCase) {
      setFormData({
        title: editingCase.title || "",
        subtitle: editingCase.subtitle || "",
        slug: editingCase.slug || "",
        content: editingCase.content || "",
        cover_image_url: editingCase.cover_image_url || "",
        tags: editingCase.tags || [],
        is_published: editingCase.is_published || false,
        featured_on_landing: editingCase.featured_on_landing || false,
      });
      setAvatarPreview(editingCase.cover_image_url || null);
    } else {
      setFormData({
        title: "",
        subtitle: "",
        slug: "",
        content: "",
        cover_image_url: "",
        tags: [],
        is_published: false,
        featured_on_landing: false,
      });
      setAvatarPreview(null);
    }
    setAvatarFile(null);
  }, [editingCase, open]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: generateSlug(title),
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Arquivo deve ser uma imagem");
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setFormData((prev) => ({ ...prev, cover_image_url: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Não autenticado");

      let coverImageUrl: string | null = formData.cover_image_url || null;
      if (avatarFile) {
        const fileExt = avatarFile.name.split(".").pop() || "jpg";
        const fileName = `${user.id}/case-avatar-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, avatarFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
        coverImageUrl = data.publicUrl;
      }

      const payload = {
        ...formData,
        cover_image_url: coverImageUrl,
        author_id: user.id,
        published_at: formData.is_published ? new Date().toISOString() : null,
      };

      if (editingCase) {
        const { error } = await supabase
          .from("site_cases")
          .update(payload)
          .eq("id", editingCase.id);
        if (error) throw error;
        toast.success("Case atualizado com sucesso!");
      } else {
        const { error } = await supabase.from("site_cases").insert(payload);
        if (error) throw error;
        toast.success("Case criado com sucesso!");
      }

      onSuccess();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error("Erro ao salvar case: " + message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCase ? "Editar Case" : "Novo Case"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Nome Profissional *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug (gerado automaticamente)</Label>
            <Input id="slug" value={formData.slug} disabled />
          </div>

          <div>
            <Label htmlFor="subtitle">Especialidade Profissional</Label>
            <Input
              id="subtitle"
              value={formData.subtitle}
              onChange={(e) => setFormData((prev) => ({ ...prev, subtitle: e.target.value }))}
            />
          </div>

          <div>
            <Label>Avatar do responsável</Label>
            <div className="flex items-center gap-4 mt-2">
              {avatarPreview || formData.cover_image_url ? (
                <div className="relative">
                  <img
                    src={avatarPreview || formData.cover_image_url || ""}
                    alt="Avatar"
                    className="w-24 h-24 rounded-full object-cover border-2 border-border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-1 -right-1 h-6 w-6 rounded-full"
                    onClick={removeAvatar}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              )}
              {!(avatarPreview || formData.cover_image_url) && (
                <span className="text-sm text-muted-foreground">
                  Clique para enviar foto (máx. 5MB)
                </span>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="content">Depoimento *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              rows={12}
              required
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <Label htmlFor="is_published">Publicado</Label>
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_published: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="featured_on_landing">Exibir na Landing Page</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Marque para exibir este case na página inicial
              </p>
            </div>
            <Switch
              id="featured_on_landing"
              checked={formData.featured_on_landing}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, featured_on_landing: checked }))
              }
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
